/**
 * Icon Upload Section Component (Shared)
 * Reusable section for uploading icons/images in config panels.
 */

import React, { useEffect } from 'react';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import { useIconUpload } from '../../../hooks/useIconUpload';

export interface IconUploadSectionProps {
  label: string;
  currentUrl: string;
  onUrlChange: (url: string) => void;
  onFileChange?: (file: File | undefined) => void;
  inputId: string;
  disabled?: boolean;
}

export const IconUploadSection = ({
  label,
  currentUrl,
  onUrlChange,
  onFileChange,
  inputId,
  disabled = false,
}: IconUploadSectionProps) => {
  const { file, previewUrl, inputRef, handleUpload, handleRemove } = useIconUpload(
    currentUrl,
    onUrlChange
  );

  // Notify parent of file changes for API save
  useEffect(() => {
    onFileChange?.(file ?? undefined);
  }, [file, onFileChange]);

  return (
    <div className="tw-space-y-2">
      <FieldTitle label={label} size="sm" />
      <div className="tw-flex tw-items-center tw-gap-3">
        {previewUrl ? (
          <div className="tw-relative tw-group">
            <img
              src={previewUrl}
              alt={label}
              className="tw-h-10 tw-w-auto tw-max-w-[120px] tw-object-contain tw-border tw-border-edge tw-rounded"
            />
            <button data-testid="shared-iconuploadsection-button-1"
              onClick={handleRemove}
              disabled={disabled}
              className={`tw-absolute tw-top-0 tw-right-0 tw-transform tw-translate-x-1/2 -tw-translate-y-1/2 tw-bg-tone-danger-bold tw-text-ink-inverse tw-rounded-full tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-text-xs tw-opacity-0 group-hover:tw-opacity-100 tw-transition-opacity ${disabled ? 'tw-pointer-events-none' : ''}`}
              title="Remove"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="tw-h-10 tw-w-20 tw-border tw-border-dashed tw-border-edge tw-rounded tw-flex tw-items-center tw-justify-center tw-text-ink-tertiary tw-text-xs">
            No image
          </div>
        )}
        <label className={`tw-px-3 tw-py-1 tw-text-sm tw-border tw-border-edge tw-text-accent tw-rounded tw-transition-colors ${disabled ? 'tw-pointer-events-none tw-cursor-default' : 'tw-cursor-pointer hover:tw-bg-accent-tint hover:tw-border-accent-hover'}`}>
          Upload
          <input data-testid="shared-iconuploadsection-input-2"
            ref={inputRef}
            id={inputId}
            type="file"
            accept="image/*"
            onChange={handleUpload}
            disabled={disabled}
            className="tw-hidden"
          />
        </label>
      </div>
    </div>
  );
};

export default IconUploadSection;
